import { useEffect } from 'react'
import { CheckCircle, XCircle, AlertCircle, X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ToastProps {
  type: 'success' | 'error' | 'info'
  title: string
  message?: string
  onClose: () => void
  duration?: number
  position?: 'top-right' | 'center'
}

export function Toast({ 
  type, 
  title, 
  message, 
  onClose, 
  duration = 3000,
  position = 'top-right' 
}: ToastProps) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose()
    }, duration)

    return () => clearTimeout(timer)
  }, [duration, onClose])

  return (
    <div className={cn(
      "fixed z-50 animate-in fade-in slide-in-from-top-1 duration-300",
      {
        "top-4 right-4": position === 'top-right',
        "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2": position === 'center'
      }
    )}>
      <div className={cn(
        "rounded-lg shadow-lg p-4 backdrop-blur-sm max-w-sm w-full border-2",
        {
          'bg-green-50/90 border-green-200': type === 'success',
          'bg-red-50/90 border-red-200': type === 'error',
          'bg-blue-50/90 border-blue-200': type === 'info',
        }
      )}>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            {type === 'success' && (
              <CheckCircle className="h-6 w-6 text-green-600" />
            )}
            {type === 'error' && (
              <XCircle className="h-6 w-6 text-red-600" />
            )}
            {type === 'info' && (
              <AlertCircle className="h-6 w-6 text-blue-600" />
            )}
          </div>
          <div className="flex-1 pt-0.5">
            <p className={cn(
              "text-sm font-medium",
              {
                'text-green-900': type === 'success',
                'text-red-900': type === 'error',
                'text-blue-900': type === 'info',
              }
            )}>
              {title}
            </p>
            {message && (
              <p className={cn(
                "mt-1 text-sm",
                {
                  'text-green-800': type === 'success',
                  'text-red-800': type === 'error',
                  'text-blue-800': type === 'info',
                }
              )}>
                {message}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className={cn(
              "flex-shrink-0 rounded-full p-1 transition-colors",
              {
                'text-green-600 hover:bg-green-100': type === 'success',
                'text-red-600 hover:bg-red-100': type === 'error',
                'text-blue-600 hover:bg-blue-100': type === 'info',
              }
            )}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}